import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminContext } from '../contexts/admin.context';
import { createModerator, deleteModerator, getModerators, updateModerator } from '../service';
import { getTranslation } from '../utils/translation';
import { ModeratorForm } from '../components/forms';
import { POPUP_DEFAULT_VALUES } from '../data/default-values';
import { FaCheckCircle, FaEdit, FaHourglass } from 'react-icons/fa';
import Spinner from '../components/common/Spinner';
import { HazardIcon, PlusIcon } from '../components/icons';
import { toast } from 'react-toastify';
import Button from '../components/common/Button';

const Moderators = () => {
  const { t } = useTranslation();
  const { user, setContentPopupInfo, setPopupInfo } = useContext(AdminContext);
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchModerators = async () => {
    setLoading(true);
    try {
      const res = await getModerators();
      setModerators(res.data || []);
    } catch (e) {
      setModerators([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (email) => {
    try {
      const res = await getModerators(email);
      const mod = res.data || {};
      setContentPopupInfo({
        isVisible: true,
        maxWidth: 'max-w-3xl',
        content: (
          <ModeratorForm
            storedData={mod}
            onSubmit={async (values) => {
              try {
                await updateModerator(values);
                await fetchModerators();
                setContentPopupInfo(POPUP_DEFAULT_VALUES);
              } catch (e) {
                console.log(e);
              }
            }}
            onDelete={async (id) => {
              try {
                setPopupInfo({
                  isVisible: true,
                  zIndex: 51,
                  icon: HazardIcon,
                  heading: getTranslation(t, 'moderators.deleteModerator'),
                  description: getTranslation(t, 'moderators.deleteModeratorDescription'),
                  action: {
                    text: getTranslation(t, 'moderators.deleteModeratorConfirm'),
                    onClick: async () => {
                      try {
                        await deleteModerator(id);
                        await fetchModerators();
                        setPopupInfo(POPUP_DEFAULT_VALUES);
                        setContentPopupInfo(POPUP_DEFAULT_VALUES);
                      } catch (e) {
                        console.log(e);
                        toast.error(getTranslation(t, 'moderators.deleteModeratorError'));
                      }
                    }
                  },
                  cancelButton: {
                    text: getTranslation(t, 'moderators.deleteModeratorCancel'),
                    onClick: () => {
                      setPopupInfo(POPUP_DEFAULT_VALUES);
                    }
                  }
                });
              } catch (e) {
                console.log(e);
              }
            }}
          />
        )
      });
    } catch {}
  };

  const handleModeratorFormSubmit = async (values) => {
    try {
      console.log('values', values, { user });
      await createModerator({ ...values, languageId: user?.languageId });
      fetchModerators();
      setContentPopupInfo(POPUP_DEFAULT_VALUES);
    } catch (e) {
      console.error('Filed to create moderator', e);
      const errorMessage =
        e?.response?.data?.message?.detail ||
        getTranslation(t, 'createProject.errors.failedCreateModerator');
      toast.error(errorMessage);
    }
  };

  const handleAddModerator = () => {
    setContentPopupInfo({
      isVisible: true,
      maxWidth: 'max-w-3xl',
      content: (
        <ModeratorForm
          setContentPopupInfo={setContentPopupInfo}
          onSubmit={handleModeratorFormSubmit}
        />
      )
    });
  };

  useEffect(() => {
    fetchModerators();
  }, []);

  return (
    <div className="overflow-y-auto w-full mini-scrollbar pr-4">
      <div className="mb-14">
        <h1
          className="text-2xl font-bold text-left text-cblack-primary mb-2.5"
          dangerouslySetInnerHTML={{ __html: getTranslation(t, 'moderators.title') }}
        />
      </div>
      <div className="flex flex-col gap-8">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <Spinner size={45} />
          </div>
        ) : moderators.length > 0 ? (
          <>
            {moderators.map((mod, idx) => (
              <div
                key={mod.email || idx}
                className="bg-gray-100 rounded-xl p-6 flex flex-col gap-2">
                <div className="flex items-center gap-3 mb-2">
                  <button
                    onClick={() => handleEdit(mod.email)}
                    className="text-gray-600 hover:text-black">
                    <FaEdit className="w-5 h-5" />
                  </button>
                  <span className="font-bold text-lg">
                    {mod.firstName} {mod.lastName}
                  </span>
                </div>
                <div className="flex items-center justify-between bg-gray-200 rounded-lg px-4 py-3">
                  <span className="text-gray-700">{mod.email}</span>
                  {mod.verified ? (
                    <FaCheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <FaHourglass className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            ))}

            <Button
              buttonType="login"
              onClick={() => handleAddModerator()}
              extraClass="w-fit mb-20">
              {getTranslation(t, 'moderators.addModerator')}
            </Button>
          </>
        ) : (
          <div className="flex justify-center items-center flex-col gap-4 h-96 w-full max-w-3xl mx-auto">
            <PlusIcon
              bg="#B5D2D3"
              className="w-24 h-24 cursor-pointer transition-all duration-300 hover:scale-110 active:scale-95"
              onClick={() => handleAddModerator()}
            />
            <div
              className="text-[#515151] text-center text-lg font-medium"
              dangerouslySetInnerHTML={{
                __html: getTranslation(t, 'moderators.noModeratorsFound')
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Moderators;
